import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {useParams, useNavigate} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {useAppSelector} from 'src/redux/hooks';
import {selectProperty} from 'src/redux/slice/propertySlice';
import {getApartmentById} from 'src/redux/slice/propertySlice';
import {
  buildingServiceFind,
  roomServiceFind,
  roomServiceUpdate,
  roomServiceDelete,
} from 'src/apis/autoGenerate/services';
import {Building, Room, RoomStatusEnum, RoomUpdateReq} from 'src/apis/autoGenerate/types';
import {formatDate} from 'src/utils/common';
import Spin from 'src/components/Spin';
import AddDirectoryModal from 'src/components/Modals/AddDirectory';
import message from 'src/components/Message';
import {PageHeader, StatusBadge, EmptyState, getOccupancyStatusVariant} from 'src/components/ui';

export default function CommercialDetail() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const property = useAppSelector(selectProperty);

  const [loading, setLoading] = useState(false);
  const [showAddDirectory, setShowAddDirectory] = useState(false);
  const [directories, setDirectories] = useState<Room[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingRoomTotals, setBuildingRoomTotals] = useState<Record<string, number>>({});
  const [totalDirectories, setTotalDirectories] = useState(0);

  // Manual pagination (pageNum is 1-based)
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Menu and modal states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDirectory, setSelectedDirectory] = useState<Room | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Add a new state to store the directory ID being edited
  const [editingDirectoryId, setEditingDirectoryId] = useState<string | null>(null);

  // Edit form interface
  interface EditFormData {
    name: string;
    description: string;
    status: RoomStatusEnum;
  }

  // Edit form state
  const [editForm, setEditForm] = useState<EditFormData>({
    name: '',
    description: '',
    status: RoomStatusEnum.Unknown,
  });

  // Get current apartment/property data
  const currentProperty = useMemo(() => {
    return property.apartmentList.find(apt => apt.id === id);
  }, [property.apartmentList, id]);

  const fetchDirectoriesPage = useCallback(
    async (
      targetPageNum: number,
      targetPageSize: number,
      buildingsList: Building[],
      totals: Record<string, number>
    ) => {
      const globalStart = (targetPageNum - 1) * targetPageSize;
      const globalEnd = globalStart + targetPageSize; // exclusive

      const neededBuildings: Array<{
        buildingId: string;
        localPageNum: number;
        sliceFrom: number;
        sliceLength: number;
      }> = [];

      let cumulative = 0;
      for (const building of buildingsList) {
        const buildingId = building.id;
        if (!buildingId) continue;

        const totalForBuilding = totals[buildingId] ?? 0;
        const buildingStart = cumulative;
        const buildingEnd = cumulative + totalForBuilding;

        if (buildingEnd <= globalStart) {
          cumulative = buildingEnd;
          continue;
        }

        if (buildingStart >= globalEnd) break;

        const localStart = Math.max(0, globalStart - buildingStart);
        const localEnd = Math.min(totalForBuilding, globalEnd - buildingStart);
        const localCountNeeded = localEnd - localStart;

        if (localCountNeeded > 0) {
          const localPageNum = Math.floor(localStart / targetPageSize) + 1;
          neededBuildings.push({
            buildingId,
            localPageNum,
            sliceFrom: localStart % targetPageSize,
            sliceLength: localCountNeeded,
          });
        }

        cumulative = buildingEnd;
      }

      const roomPageRequests = neededBuildings.map(async item => {
        const roomsRes = await roomServiceFind(
          item.buildingId,
          undefined,
          targetPageSize,
          item.localPageNum
        );
        const rooms = roomsRes.data.rooms || [];
        return rooms.slice(item.sliceFrom, item.sliceFrom + item.sliceLength);
      });

      const parts = await Promise.all(roomPageRequests);
      return parts.flat();
    },
    []
  );

  const initAndLoadPage = useCallback(
    async (targetPageNum: number, targetPageSize: number) => {
      if (!id) return;

      setLoading(true);
      try {
        const buildingsRes = await buildingServiceFind(undefined, undefined, [id]);
        const buildingsList = (buildingsRes.data.buildings || []) as Building[];

        // Keep backend order: room slicing depends on the same building order.
        setBuildings(buildingsList);

        const totalsEntries = await Promise.all(
          buildingsList.map(async b => {
            if (!b.id) return [b.id, 0] as const;
            const roomsRes = await roomServiceFind(b.id, undefined, 1, 1);
            return [b.id, roomsRes.data.total ?? 0] as const;
          })
        );

        const totalsObj = totalsEntries.reduce<Record<string, number>>((acc, entry) => {
          const [buildingId, total] = entry;
          if (buildingId) acc[buildingId] = total;
          return acc;
        }, {});

        setBuildingRoomTotals(totalsObj);
        const nextTotalDirectories = Object.values(totalsObj).reduce((sum, n) => sum + n, 0);
        setTotalDirectories(nextTotalDirectories);

        const maxPage = Math.max(1, Math.ceil(nextTotalDirectories / targetPageSize));
        const clampedPageNum = Math.min(Math.max(1, targetPageNum), maxPage);
        setPageNum(clampedPageNum);

        const pageRooms = await fetchDirectoriesPage(
          clampedPageNum,
          targetPageSize,
          buildingsList,
          totalsObj
        );
        setDirectories(pageRooms);
      } catch (error) {
        console.error('Error loading directories:', error);
      } finally {
        setLoading(false);
      }
    },
    [fetchDirectoriesPage, id]
  );

  useEffect(() => {
    if (id) {
      getApartmentById(id);
      initAndLoadPage(1, pageSize);
    }
  }, [id, initAndLoadPage, pageSize]);

  const handleAddDirectoryComplete = () => {
    setShowAddDirectory(false);
    initAndLoadPage(pageNum, pageSize); // Refresh totals + current page
  };

  // Menu handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, directory: Room) => {
    setAnchorEl(event.currentTarget);
    setSelectedDirectory(directory);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDirectory(null);
  };

  // Edit handlers
  const handleEditClick = () => {
    if (selectedDirectory) {
      setEditForm({
        name: selectedDirectory.name || '',
        description: selectedDirectory.description || '',
        status: selectedDirectory.status || RoomStatusEnum.Unknown,
      });
      setEditingDirectoryId(selectedDirectory.id || null);
      setShowEditModal(true);
    }
    handleMenuClose();
  };

  // Update handleEditSubmit to include buildingId and other original fields
  const handleEditSubmit = async () => {
    console.log('handleEditSubmit called');
    console.log('editingDirectoryId:', editingDirectoryId);
    console.log('editForm:', editForm);

    if (!editingDirectoryId) {
      console.log('Early return: editingDirectoryId is falsy');
      message.error('Directory ID not found');
      return;
    }

    // Find the original directory data to get buildingId and other fields
    const originalDirectory = directories.find(dir => dir.id === editingDirectoryId);
    if (!originalDirectory) {
      message.error('Original directory data not found');
      return;
    }

    console.log('Original directory:', originalDirectory);
    console.log('Starting update process...');
    setEditLoading(true);

    try {
      const updateReq: RoomUpdateReq = {
        room: {
          ...originalDirectory, // Include all original fields
          // Override with updated fields
          name: editForm.name,
          description: editForm.description,
          status: editForm.status,
          // buildingId is preserved from originalDirectory
        },
      };

      console.log('Update request:', updateReq);
      console.log('Room ID:', editingDirectoryId);

      const response = await roomServiceUpdate(editingDirectoryId, updateReq);
      console.log('API response:', response);

      message.success('Directory updated successfully');
      setShowEditModal(false);
      setEditingDirectoryId(null);
      initAndLoadPage(pageNum, pageSize); // Refresh totals + current page
    } catch (error: any) {
      console.error('Error updating directory:', error);

      // Use the same error extraction pattern as AddDirectoryModal
      let errorMessage = 'Failed to update directory';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      message.error(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  // Update handleEditCancel to clear the stored directory ID
  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditForm({
      name: '',
      description: '',
      status: RoomStatusEnum.Unknown,
    });
    setEditingDirectoryId(null); // Clear the stored ID
  };

  // Delete handler
  const handleDeleteClick = async () => {
    if (!selectedDirectory?.id) return;

    setLoading(true);
    handleMenuClose();

    try {
      await roomServiceDelete(selectedDirectory.id);
      message.success('Directory deleted successfully');
      initAndLoadPage(pageNum, pageSize); // Refresh totals + current page
    } catch (error: any) {
      console.error('Error deleting directory:', error);

      // Extract error message from API response
      let errorMessage = 'Failed to delete directory';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status?: string): string => {
    switch (status) {
      case RoomStatusEnum.Occupied:
        return 'Occupied';
      case RoomStatusEnum.Vacant:
        return 'Vacant';
      default:
        return 'Unknown';
    }
  };

  if (!currentProperty) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <Spin spinning={true} />
      </Box>
    );
  }

  return (
    <Box>
      <Spin spinning={loading} />

      <PageHeader
        title={currentProperty.name || 'Commercial Property'}
        subtitle="Commercial Property"
        backLink={{label: 'Back to Properties', href: '/property'}}
      >
        {currentProperty.description && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <LocationOnOutlinedIcon sx={{fontSize: 18, color: 'text.secondary'}} />
            <Typography variant="body2" color="text.secondary">
              {currentProperty.description}
            </Typography>
          </Box>
        )}
      </PageHeader>

      {/* Content */}
      {totalDirectories === 0 && !loading ? (
        <EmptyState
          title="No directories yet"
          description="Add a directory to get started with your commercial property."
          action={{
            label: 'Add Directory',
            onClick: () => setShowAddDirectory(true),
            icon: <AddIcon />,
          }}
        />
      ) : (
        <>
          <Grid container justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Directories ({totalDirectories})</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setShowAddDirectory(true)}
            >
              Add Directory
            </Button>
          </Grid>

          <Table sx={{minWidth: 650}} aria-label="directories table" size="small">
            <TableHead>
              <TableRow>
                <TableCell width={300}>Directory Name</TableCell>
                <TableCell width={150}>Status</TableCell>
                <TableCell width={200}>Created Date</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {directories.map(directory => (
                <TableRow key={directory.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {directory.name || 'Unnamed Directory'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={getOccupancyStatusVariant(directory.status)}
                      label={getStatusLabel(directory.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {directory.createdAt ? formatDate(directory.createdAt) : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={e => handleMenuClick(e, directory)}>
                      <MoreHorizIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalDirectories > 0 && (
            <TablePagination
              component="div"
              count={totalDirectories}
              page={pageNum - 1}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[5, 10, 20, 50]}
              onPageChange={(_e, newPage) => {
                const nextPageNum = newPage + 1;
                setPageNum(nextPageNum);
                fetchDirectoriesPage(nextPageNum, pageSize, buildings, buildingRoomTotals)
                  .then(pageRooms => setDirectories(pageRooms))
                  .catch(err => console.error('Error loading directory page:', err));
              }}
              onRowsPerPageChange={e => {
                const nextSize = Number(e.target.value);
                setPageSize(nextSize);
                setPageNum(1);
                fetchDirectoriesPage(1, nextSize, buildings, buildingRoomTotals)
                  .then(pageRooms => setDirectories(pageRooms))
                  .catch(err => console.error('Error loading directory page:', err));
              }}
            />
          )}
        </>
      )}

      {/* Add Directory Modal */}
      {showAddDirectory && (
        <AddDirectoryModal
          open={showAddDirectory}
          onClose={() => setShowAddDirectory(false)}
          onComplete={handleAddDirectoryComplete}
          propertyId={id!}
          buildings={buildings}
        />
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Directory</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{color: 'error.main'}}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{color: 'error.main'}} />
          </ListItemIcon>
          <ListItemText>Delete Directory</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Directory Modal */}
      <Dialog open={showEditModal} onClose={handleEditCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Directory</DialogTitle>
        <DialogContent>
          <Box sx={{pt: 2}}>
            <TextField
              fullWidth
              label="Directory Name"
              value={editForm.name}
              onChange={e => setEditForm({...editForm, name: e.target.value})}
              margin="normal"
              size="small"
            />

            <TextField
              fullWidth
              label="Description"
              value={editForm.description}
              onChange={e => setEditForm({...editForm, description: e.target.value})}
              margin="normal"
              size="small"
              multiline
              rows={2}
            />

            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={e => setEditForm({...editForm, status: e.target.value as RoomStatusEnum})}
              >
                <MenuItem value={RoomStatusEnum.Unknown}>Unknown</MenuItem>
                <MenuItem value={RoomStatusEnum.Vacant}>Vacant</MenuItem>
                <MenuItem value={RoomStatusEnum.Occupied}>Occupied</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              console.log('Update button clicked!'); // Debug log
              console.log('Button disabled:', editLoading || !editForm.name.trim()); // Debug log
              console.log('editLoading:', editLoading); // Debug log
              console.log('editForm.name.trim():', editForm.name.trim()); // Debug log
              handleEditSubmit();
            }}
            variant="contained"
            disabled={editLoading || !editForm.name.trim()}
          >
            {editLoading ? 'Updating...' : 'Update Directory'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
